import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Product } from "@/components/DesignResults";
import { track } from "@/lib/posthog";
import { trackBrevo } from "@/lib/brevo";

export const FREE_TRACK_LIMIT = 3;
export const PRO_TRACK_LIMIT = 25;
export const FREE_HISTORY_DAYS = 30;

export type TrackedPrice = {
  id: string;
  saved_item_id: string;
  product_title: string;
  product_source: string;
  currency: string;
  baseline_price: number | null;
  current_price: number | null;
  status: string;
  availability: string | null;
  last_checked_at: string | null;
  created_at: string;
};

export const STOCK_LABEL: Record<string, string> = {
  in_stock: "In stock",
  low_stock: "Low stock",
  out_of_stock: "Out of stock",
  preorder: "Pre-order",
};


export function productKey(p: { title: string; source: string }) {
  return `${p.title}|||${p.source}`;
}

export function usePriceTracking(isPro: boolean) {
  const [userId, setUserId] = useState<string | null>(null);
  const [tracked, setTracked] = useState<TrackedPrice[]>([]);
  const [savedIds, setSavedIds] = useState<Record<string, string>>({});
  const [optIn, setOptIn] = useState(false);
  const [loading, setLoading] = useState(true);

  const limit = isPro ? PRO_TRACK_LIMIT : FREE_TRACK_LIMIT;

  const load = useCallback(async () => {
    const { data: userData } = await supabase.auth.getUser();
    const uid = userData.user?.id ?? null;
    setUserId(uid);
    if (!uid) {
      setLoading(false);
      return;
    }

    const [savedRes, trackedRes, profileRes] = await Promise.all([
      supabase.from("saved_items").select("id, product_title, product_source"),
      supabase
        .from("tracked_prices")
        .select(
          "id, saved_item_id, product_title, product_source, currency, baseline_price, current_price, status, availability, last_checked_at, created_at",
        ),
      supabase
        .from("profiles")
        .select("price_alert_opt_in")
        .eq("user_id", uid)
        .maybeSingle(),
    ]);

    const map: Record<string, string> = {};
    (savedRes.data ?? []).forEach((r: any) => {
      map[productKey({ title: r.product_title, source: r.product_source })] = r.id;
    });
    setSavedIds(map);
    setTracked((trackedRes.data ?? []) as TrackedPrice[]);
    setOptIn(Boolean(profileRes.data?.price_alert_opt_in));
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const trackedByKey = useCallback(
    (p: { title: string; source: string }) =>
      tracked.find(
        (t) => t.product_title === p.title && t.product_source === p.source,
      ) ?? null,
    [tracked],
  );

  const isTracked = useCallback(
    (p: { title: string; source: string }) => trackedByKey(p) !== null,
    [trackedByKey],
  );

  const atLimit = tracked.length >= limit;

  const enableAlerts = useCallback(async () => {
    if (!userId) return;
    setOptIn(true);
    await supabase
      .from("profiles")
      .update({
        price_alert_opt_in: true,
        price_alert_opt_in_at: new Date().toISOString(),
      })
      .eq("user_id", userId);
    track("price_alerts_opted_in", {});
    void trackBrevo("price_alerts_opted_in", {});
  }, [userId]);

  const setAlertsEnabled = useCallback(
    async (enabled: boolean) => {
      if (!userId) return;
      setOptIn(enabled);
      await supabase
        .from("profiles")
        .update({
          price_alert_opt_in: enabled,
          price_alert_opt_in_at: enabled ? new Date().toISOString() : null,
        })
        .eq("user_id", userId);
      track(enabled ? "price_alerts_opted_in" : "price_alerts_opted_out", {});
    },
    [userId],
  );

  /** Returns "tracked" | "untracked" | "limit" | "error" */
  const toggleTracking = useCallback(
    async (product: Product): Promise<"tracked" | "untracked" | "limit" | "error"> => {
      if (!userId) return "error";
      const existing = trackedByKey(product);

      if (existing) {
        setTracked((prev) => prev.filter((t) => t.id !== existing.id));
        const { error } = await supabase
          .from("tracked_prices")
          .delete()
          .eq("id", existing.id);
        if (error) {
          void load();
          return "error";
        }
        track("price_tracking_disabled", {
          item_id: productKey(product).toLowerCase(),
          item_brand: product.source,
        });
        return "untracked";
      }

      if (atLimit) return "limit";

      const savedItemId = savedIds[productKey(product)];
      if (!savedItemId) return "error";

      const priceNumber = (() => {
        const n = Number(String(product.price ?? "").replace(/[^0-9.]/g, ""));
        return Number.isFinite(n) && n > 0 ? n : null;
      })();

      const { data, error } = await supabase
        .from("tracked_prices")
        .insert({
          user_id: userId,
          saved_item_id: savedItemId,
          product_title: product.title,
          product_source: product.source,
          product_url: product.link || null,
          baseline_price: priceNumber,
          current_price: priceNumber,
        })
        .select(
          "id, saved_item_id, product_title, product_source, currency, baseline_price, current_price, status, availability, last_checked_at, created_at",
        )
        .single();

      if (error || !data) {
        if (error?.message?.includes("tracked_price_limit_reached")) return "limit";
        console.error("[tracked_prices] insert failed", error);
        return "error";
      }

      setTracked((prev) => [...prev, data as TrackedPrice]);
      track("price_tracking_enabled", {
        item_id: productKey(product).toLowerCase(),
        item_brand: product.source,
        item_price: priceNumber,
        tracked_total_after: tracked.length + 1,
      });
      void trackBrevo("price_tracking_enabled", {
        item_brand: product.source,
      });
      return "tracked";
    },
    [userId, trackedByKey, atLimit, savedIds, tracked.length, load],
  );

  return {
    loading,
    tracked,
    trackedCount: tracked.length,
    limit,
    atLimit,
    isTracked,
    trackedByKey,
    toggleTracking,
    optIn,
    enableAlerts,
    setAlertsEnabled,
    reload: load,
  };
}
